<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Business Entities" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :routeName="businessEntityCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <BusinessEntityFilters
        class="px-5 mb-sm-5"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></BusinessEntityFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="businessEntityList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editBusinessEntity($event, item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              class="mx-1 my-1"
              color="red"
              outlined
              @click="deleteBusinessEntityHandler(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected business entity?'"
      :toolbarColor="'primary'"
      @cancel-click="cancelDelete"
      @ok-click="confirmDelete"
      :showModal="showDeleteConfirmationDialog"
    ></ConfirmationModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { businessEntityCreateRoute, businessEntityEditRoute } from '@/lib/constants/routes'
import BusinessEntityFilters from './BusinessEntityFilters'
import { BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ConfirmationModal } from '@/components/dialogs'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    BusinessEntityFilters,
    BottomSpacer,
    ButtonCustomIcon,
    SimplePageHeader,
    ConfirmationModal
  },
  data () {
    return {
      businessEntityList: [],
      businessEntityCreateRoute,
      showDeleteConfirmationDialog: false,
      businessEntityIdToDelete: null,
      headers: [
        { text: 'Business Entity Name', value: 'businessEntityName', width: '70%' },
        { text: 'Actions', value: 'action', align: 'center', class: 'text-xs-right', width: '30%', sortable: false }
      ],
      hasFetchedAllBusinessEntities: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['businessEntityName'],
        sortDesc: [false]
      }
    }
  },
  computed: {
    ...mapFields('businessEntities', [
      'totalRecords'
    ]),
    ...mapFields('businessEntityFilters', [
      'id'
    ]),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchBusinessEntities(newOptions, previousOptions)) {
          return
        }

        this.fetchBusinessEntities()
      },
      deep: true
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  },
  methods: {
    ...mapActions('businessEntities', [
      'getBusinessEntitiesWithFiltersPaginated',
      'getFieldsWithFilters'
    ]),
    ...mapActions('businessEntity', [
      'deleteBusinessEntity'
    ]),
    ...mapActions('businessEntityFilters', [
      'clearFilters'
    ]),
    editBusinessEntity (event, businessEntity) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: businessEntityEditRoute.name, params: { id: businessEntity.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: businessEntityEditRoute.name, params: { id: businessEntity.id } })
      }
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { businessEntityId, id } = this.$route.query

      this.id = businessEntityId ? Number(businessEntityId) : (id ? Number(id) : null)
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)

      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.id)
      setOrDeleteQueryParam(nextQuery, 'businessEntityName', null)
      setOrDeleteQueryParam(nextQuery, 'id', null)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'businessEntityId'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchBusinessEntities () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      if (itemsPerPage === -1) {
        this.getFieldsWithFilters().then(businessEntities => {
          this.hasFetchedAllBusinessEntities = true
          this.businessEntityList = businessEntities
        })
        return
      }

      this.hasFetchedAllBusinessEntities = false
      this.getBusinessEntitiesWithFiltersPaginated({
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(businessEntities => {
        this.businessEntityList = businessEntities
      })
    },
    shouldFetchBusinessEntities (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllBusinessEntities)
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
    },
    deleteBusinessEntityHandler (businessEntity) {
      this.businessEntityIdToDelete = businessEntity.id
      this.showDeleteConfirmationDialog = true
    },
    cancelDelete () {
      this.showDeleteConfirmationDialog = false
      this.businessEntityIdToDelete = null
    },
    confirmDelete () {
      this.showDeleteConfirmationDialog = false
      this.deleteBusinessEntity(this.businessEntityIdToDelete).then(() => {
        this.businessEntityIdToDelete = null
        this.fetchBusinessEntities()
      })
    }
  }
}
</script>
